import { findContainer } from "../../pages/dashboard/route-planner/helpers";
import { Active, Over } from "@dnd-kit/core";
import { useState } from "react";
import {
  AdminGetRoutesLocationsResDto,
  AdminGetRoutesResDto,
  AdminRegionLocationResDto,
  AdminRegionResDto,
} from "service/api/estabrookSchemas";

type DropPosition = {
  x: number;
  y: number;
};

type DropIndices = {
  before?: number; // Optional - hidden when dropping at first position
  after?: number; // Optional - hidden when dropping at last position
};

type ContainerType = "region" | "route";

/**
 * Calculate drop indices based on target container's positions
 * Simple logic: Show the positions that will be before and after the dropped item
 */
const calculateDropIndices = (
  over: Over,
  active: Active,
  containerType: ContainerType,
  containerItems: AdminRegionResDto[] | AdminGetRoutesResDto[],
): DropIndices => {
  if (containerType === "region") {
    return calculateRegionDropIndices(
      over,
      active,
      containerItems as AdminRegionResDto[],
    );
  } else {
    return calculateRouteDropIndices(
      over,
      active,
      containerItems as AdminGetRoutesResDto[],
    );
  }
};

/**
 * Calculate drop indices for regions
 */
const calculateRegionDropIndices = (
  over: Over,
  active: Active,
  regions: AdminRegionResDto[],
): DropIndices => {
  // Find target region
  const targetRegion = regions.find(
    (r) => r.id === over.data.current?.regionId,
  );
  if (!targetRegion?.locations) {
    return { after: 1 }; // Dropping in empty region
  }

  const draggedItemId = active.data.current?.id;
  const overId = over.id.toString();

  // Get ALL items in the target region (including dragged item for position calculation)
  const allRegionItems = targetRegion.locations || [];

  // Get the target container items WITHOUT the dragged item (simulating final state)
  const targetItems = allRegionItems.filter(
    (loc) => loc.locationId !== draggedItemId,
  );

  // Find position of the item we're dropping over in the ACTUAL current region
  let actualPosition = -1;
  let draggedPosition = -1;
  let dropPosition = -1;

  // Find where the dragged item currently is
  draggedPosition = allRegionItems.findIndex(
    (item) => item.locationId === draggedItemId,
  );

  // Check if we're dropping over a specific item in the region
  const overItem = allRegionItems.find((l) => l.locationId === overId);
  if (overItem) {
    // Find position of this item in the ACTUAL current region (including dragged item)
    actualPosition = allRegionItems.findIndex(
      (item) => item.locationId === overId,
    );

    if (overId === draggedItemId) {
      // Dropping over the same item you're dragging - staying in same position
      if (draggedPosition === 0) {
        return targetItems.length > 0 ? { after: 1 } : {};
      } else if (draggedPosition >= allRegionItems.length - 1) {
        return targetItems.length > 0
          ? { before: targetItems.length }
          : { after: 1 };
      } else {
        // Middle position - show the items that will be before and after in final state
        return {
          before: draggedPosition, // 1-based position
          after: draggedPosition + 2, // Skip the dragged item position
        };
      }
    } else if (draggedPosition === -1) {
      // Dragged item is not in this region - simple case
      dropPosition = actualPosition;
    } else if (actualPosition < draggedPosition) {
      // Dropping before the dragged item's current position
      dropPosition = actualPosition;
    } else {
      // Dropping after the dragged item's current position
      // When we remove the dragged item, positions shift down by 1
      dropPosition = actualPosition - 1;
    }
  } else if (overId === over.data.current?.regionId) {
    // Dropping over the region container itself - goes to end
    dropPosition = targetItems.length;
  }

  if (dropPosition !== -1) {
    if (dropPosition === 0) {
      // Dropping at first position in region
      return targetItems.length > 0 ? { after: 2 } : {};
    } else if (dropPosition >= targetItems.length) {
      // Dropping at end of region
      return targetItems.length > 0
        ? { before: targetItems.length }
        : { after: 1 };
    } else {
      // Dropping in middle - calculate based on whether we're moving forward or backward
      let beforePos, afterPos;
      if (draggedPosition !== -1 && actualPosition < draggedPosition) {
        // Dropping before the dragged item's original position
        if (dropPosition === 1) {
          // Special case: dropping between positions 1 and 2
          beforePos = 1;
          afterPos = 2;
        } else {
          beforePos = dropPosition;
          afterPos = dropPosition + 1;
        }
      } else {
        // Dropping after the dragged item's original position or item not in region
        beforePos = dropPosition + 2;
        afterPos = dropPosition + 3;
      }

      return {
        before: beforePos,
        after: afterPos,
      };
    }
  }

  // Dropping at the end of the region (fallback)
  return targetItems.length > 0 ? { before: targetItems.length } : { after: 1 };
};

/**
 * Calculate drop indices for routes
 */
const calculateRouteDropIndices = (
  over: Over,
  active: Active,
  routes: AdminGetRoutesResDto[],
): DropIndices => {
  const overId = over.id.toString();

  // Determine target route and shift
  let targetRoute: AdminGetRoutesResDto | undefined;
  let targetShift: string | undefined;

  // Find the route that contains this location ID
  for (const route of routes) {
    const item = route.locations?.find((l) => l.locationId === overId);
    if (item) {
      targetRoute = route;
      targetShift = item.order?.status?.toLowerCase();
      break;
    }
  }

  // If not found as a location, check if it's a route container ID (e.g., "routeId-shift")
  if (!targetRoute && overId.includes("-")) {
    const containerId = findContainer(over.id, routes) ?? overId;
    const routeId = containerId.substring(0, containerId.lastIndexOf("-"));
    const shiftType = containerId.substring(containerId.lastIndexOf("-") + 1);
    targetRoute = routes.find((r) => r.id === routeId);
    targetShift = shiftType.toLowerCase();
  }

  const draggedItemId = active.data.current?.id;

  // Debug: Log the data we're receiving
  if (!targetRoute || !targetShift) {
    return { after: 1 }; // Fallback for unknown target
  }

  // Get items in the target shift WITHOUT the dragged item (simulating final state)
  const allShiftItems = (targetRoute.locations || []).filter((loc) => {
    const itemShift = loc.order?.status?.toLowerCase();
    return itemShift === targetShift;
  });

  const targetShiftItems = allShiftItems.filter((loc) => {
    const isDraggedItem = loc.locationId === draggedItemId;
    return !isDraggedItem;
  });

  // Calculate drop position based on overId in the target shift
  let dropPosition = -1;
  let actualPosition = -1;
  let draggedPosition = -1;

  // Check if we're dropping over a specific item in the target shift
  const overItem = targetRoute.locations?.find((l) => l.locationId === overId);
  if (overItem && overItem.order?.status?.toLowerCase() === targetShift) {
    // Find position of this item in the ACTUAL current shift (including dragged item)
    actualPosition = allShiftItems.findIndex(
      (item) => item.locationId === overId,
    );

    // Find where the dragged item currently is
    draggedPosition = allShiftItems.findIndex(
      (item) => item.locationId === draggedItemId,
    );

    if (overId === draggedItemId) {
      // Dropping over the same item you're dragging - staying in same position
      if (draggedPosition === 0) {
        return targetShiftItems.length > 0 ? { after: 1 } : {};
      } else if (draggedPosition >= allShiftItems.length - 1) {
        return targetShiftItems.length > 0
          ? { before: targetShiftItems.length }
          : { after: 1 };
      } else {
        // Middle position - show the items that will be before and after in final state
        // If item 5 stays in position 5, then item 4 will be before and item 6 will be after
        return {
          before: draggedPosition, // Item at position draggedPosition (1-based) will be before
          after: draggedPosition + 2, // Item at position draggedPosition+2 (1-based) will be after (skipping the dragged item)
        };
      }
    } else if (draggedPosition === -1) {
      // Dragged item is not in this shift - simple case
      dropPosition = actualPosition;
    } else if (actualPosition < draggedPosition) {
      // Dropping before the dragged item's current position
      dropPosition = actualPosition;
    } else {
      // Dropping after the dragged item's current position
      // When we remove the dragged item, positions shift down by 1
      dropPosition = actualPosition - 1;
    }
  } else if (overId.includes("-") && overId.endsWith("-" + targetShift)) {
    // Dropping over the shift container itself - goes to end
    dropPosition = targetShiftItems.length;
  }

  if (dropPosition !== -1) {
    if (dropPosition === 0) {
      // Dropping at first position in shift
      return targetShiftItems.length > 0 ? { after: 2 } : {};
    } else if (dropPosition >= targetShiftItems.length) {
      // Dropping at end of shift
      return targetShiftItems.length > 0
        ? { before: targetShiftItems.length }
        : { after: 1 };
    } else {
      // Dropping in middle - check if we're dropping before or after the dragged item's original position
      let beforePos, afterPos;
      if (actualPosition < draggedPosition) {
        // Dropping before the dragged item's original position
        if (dropPosition === 1) {
          // Special case: dropping between positions 1 and 2
          beforePos = 1;
          afterPos = 2;
        } else {
          beforePos = dropPosition;
          afterPos = dropPosition + 1;
        }
      } else {
        // Dropping after the dragged item's original position
        beforePos = dropPosition + 2;
        afterPos = dropPosition + 3;
      }

      return {
        before: beforePos,
        after: afterPos,
      };
    }
  }

  // Dropping at the end of the shift
  return targetShiftItems.length > 0
    ? { before: targetShiftItems.length }
    : { after: 1 };
};

export const useDragTooltip = (containerType: ContainerType) => {
  const [dropPosition, setDropPosition] = useState<DropPosition>({
    x: 0,
    y: 0,
  });
  const [dropIndices, setDropIndices] = useState<DropIndices | null>(null);
  // Regions
  const [currentDraggedRegion, setCurrentDraggedRegion] = useState<
    AdminRegionResDto | undefined
  >(undefined);
  const [currentDraggedRegionLocation, setCurrentDraggedRegionLocation] =
    useState<AdminRegionLocationResDto | undefined>(undefined);
  // Routes
  const [currentDraggedRoute, setCurrentDraggedRoute] = useState<
    AdminGetRoutesResDto | undefined
  >(undefined);
  const [currentDraggedRouteLocation, setCurrentDraggedRouteLocation] =
    useState<AdminGetRoutesLocationsResDto | undefined>(undefined);

  const setDragDetails = (
    over: Over,
    active: Active,
    containerItems: AdminRegionResDto[] | AdminGetRoutesResDto[],
  ) => {
    // Update position using the over element's rect
    const overRect = over.rect;
    setDropPosition({
      x: overRect.left + overRect.width / 2,
      y: overRect.top - 20, // Offset to show above the drop target
    });

    // Calculate drop indices using the new simplified logic
    const dropIndices = calculateDropIndices(
      over,
      active,
      containerType,
      containerItems,
    );

    setDropIndices(dropIndices);

    if (containerType === "region") {
      const region = (containerItems as AdminRegionResDto[]).find(
        (r) => r.id === over.data.current?.regionId,
      );
      const itemData = region?.locations?.find(
        (l) => l.locationId === active.data.current?.id,
      );
      setCurrentDraggedRegion(region);
      setCurrentDraggedRegionLocation(itemData);
    } else if (containerType === "route") {
      // Enhanced route detection for better drop zone handling
      const overId =
        findContainer(over.id, containerItems as AdminGetRoutesResDto[]) ??
        over.id.toString();
      const routeId = overId.includes("-")
        ? overId.substring(0, overId.lastIndexOf("-"))
        : overId;

      // Try multiple ways to find the correct route
      let route = (containerItems as AdminGetRoutesResDto[]).find(
        (r) => r.id === routeId,
      );

      if (!route && over.data.current?.routeId) {
        route = (containerItems as AdminGetRoutesResDto[]).find(
          (r) => r.id === over.data.current?.routeId,
        );
      }

      if (!route) {
        // Fallback: find route by checking if any of its locations match the active item
        route = (containerItems as AdminGetRoutesResDto[]).find((r) =>
          r.locations?.some((l) => l.locationId === active.data.current?.id),
        );
      }

      const itemData = route?.locations?.find(
        (l) => l.locationId === active.data.current?.id,
      );
      setCurrentDraggedRoute(route);
      setCurrentDraggedRouteLocation(itemData);
    }
  };

  return {
    setDropIndices,
    setDropPosition,
    setDragDetails,
    dropPosition,
    dropIndices,
    currentDraggedRegion,
    currentDraggedRegionLocation,
    currentDraggedRoute,
    currentDraggedRouteLocation,
  };
};
